import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, FileText, Pencil, Trash2, AlertTriangle, Search, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const DOC_TYPES = ['Proposta Comercial', 'ETP', 'TR', 'Edital', 'Todos'];
const DOC_TYPES_SELECTABLE = DOC_TYPES.filter(t => t !== 'Todos');
const FIELD_TYPES = [
  { value: 'texto', label: 'Texto curto' },
  { value: 'textarea', label: 'Texto longo' },
  { value: 'numero', label: 'Numero' },
  { value: 'data', label: 'Data' },
  { value: 'sim_nao', label: 'Sim/Nao' },
];
const AUTO_FILL_OPTIONS = [
  { value: '', label: 'Nenhum (manual)' },
  { value: 'client.razao_social', label: 'Cliente: Razao Social' },
  { value: 'client.cnpj', label: 'Cliente: CNPJ' },
  { value: 'client.endereco', label: 'Cliente: Endereco' },
  { value: 'client.cidade', label: 'Cliente: Cidade' },
  { value: 'client.uf', label: 'Cliente: UF' },
  { value: 'client.esfera', label: 'Cliente: Esfera' },
  { value: 'client.poder', label: 'Cliente: Poder' },
  { value: 'system.data_atual', label: 'Sistema: Data atual' },
  { value: 'system.ano_atual', label: 'Sistema: Ano atual' },
];

const EMPTY_FORM = { name: '', label: '', description: '', field_type: 'texto', default_value: '', auto_fill_source: '', doc_types: [] };

const DocVariablesPage = () => {
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');

  const loadVariables = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/doc-variables`, { withCredentials: true });
      setVariables(res.data);
    } catch (error) {
      console.error('Failed to load:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVariables(); }, [loadVariables]);

  const openCreate = () => { setEditing(null); setFormData({ ...EMPTY_FORM }); setDialogOpen(true); };

  const openEdit = (v) => {
    setEditing(v);
    setFormData({
      name: v.name, label: v.label, description: v.description || '',
      field_type: v.field_type || 'texto', default_value: v.default_value || '',
      auto_fill_source: v.auto_fill_source || '', doc_types: v.doc_types || [],
    });
    setDialogOpen(true);
  };

  const toggleDocType = (docType) => {
    const exists = formData.doc_types.find(dt => dt.doc_type === docType);
    if (exists) {
      setFormData({ ...formData, doc_types: formData.doc_types.filter(dt => dt.doc_type !== docType) });
    } else {
      setFormData({ ...formData, doc_types: [...formData.doc_types, { doc_type: docType, required: false }] });
    }
  };

  const toggleRequired = (docType) => {
    setFormData({
      ...formData,
      doc_types: formData.doc_types.map(dt =>
        dt.doc_type === docType ? { ...dt, required: !dt.required } : dt
      )
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (formData.doc_types.length === 0) { toast.error('Selecione ao menos um tipo de documento'); return; }
    setSubmitting(true);
    try {
      if (editing) {
        const { name, ...updateData } = formData;
        await axios.put(`${API_URL}/doc-variables/${editing.var_id}`, updateData, { withCredentials: true });
        toast.success('Variavel atualizada');
      } else {
        await axios.post(`${API_URL}/doc-variables`, formData, { withCredentials: true });
        toast.success('Variavel criada');
      }
      setDialogOpen(false);
      loadVariables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (varId) => {
    try {
      await axios.delete(`${API_URL}/doc-variables/${varId}`, { withCredentials: true });
      toast.success('Variavel excluida');
      setDeleteTarget(null);
      loadVariables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const filtered = variables.filter(v => {
    const matchSearch = !search || v.name.includes(search.toUpperCase()) || v.label.toLowerCase().includes(search.toLowerCase());
    const matchType = !filterType || v.doc_types?.some(dt => dt.doc_type === filterType || dt.doc_type === 'Todos');
    return matchSearch && matchType;
  });

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="doc-variables-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Variaveis de Documento</h1>
          <p className="text-[#6E6E73]">Campos para preenchimento nos documentos, configurados por tipo</p>
        </div>
        <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-doc-variable-button">
          <Plus className="w-4 h-4 mr-2" />Nova Variavel
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#AEAEB2]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="pl-10" />
        </div>
        <Select value={filterType || 'all'} onValueChange={(val) => setFilterType(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-52"><SelectValue placeholder="Tipo de documento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {DOC_TYPES_SELECTABLE.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length > 0 ? (
        <div className="border border-[#E5E5EA] rounded-xl overflow-hidden bg-white">
          <table className="w-full">
            <thead className="bg-[#F7F7F8]">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">VARIAVEL</th>
                <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">DESCRICAO</th>
                <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">TIPO CAMPO</th>
                <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">TIPOS DE DOC</th>
                <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">AUTO</th>
                <th className="text-right p-3 text-xs font-medium text-[#6E6E73]">ACOES</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.var_id} className="border-t border-[#E5E5EA] hover:bg-[#F7F7F8]" data-testid={`doc-var-row-${v.name}`}>
                  <td className="p-3">
                    <code className="text-sm font-bold text-[#AF2628] font-mono">{v.name}</code>
                    <p className="text-xs text-[#6E6E73]">{v.label}</p>
                  </td>
                  <td className="p-3 text-sm text-[#6E6E73] max-w-48 truncate">{v.description || '-'}</td>
                  <td className="p-3">
                    <span className="text-xs bg-[#F7F7F8] px-2 py-1 rounded">{FIELD_TYPES.find(f => f.value === v.field_type)?.label || v.field_type}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(v.doc_types || []).map(dt => (
                        <span key={dt.doc_type} className={`text-[10px] px-1.5 py-0.5 rounded ${dt.required ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-blue-50 text-blue-600 border border-blue-200'}`}>
                          {dt.doc_type}{dt.required ? ' *' : ''}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    {v.auto_fill_source ? (
                      <span className="text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded border border-green-200">{v.auto_fill_source}</span>
                    ) : <span className="text-xs text-[#AEAEB2]">Manual</span>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(v)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(v)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-white border border-[#E5E5EA] rounded-xl">
          <FileText className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
          <p className="text-[#6E6E73] mb-4">Nenhuma variavel de documento cadastrada</p>
          <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]"><Plus className="w-4 h-4 mr-2" />Criar Primeira</Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Editar Variavel' : 'Nova Variavel de Documento'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="doc-variable-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome *</Label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} placeholder="Ex: OBJETO" className="font-mono" disabled={!!editing} data-testid="doc-var-name" />
              </div>
              <div>
                <Label>Tipo de campo</Label>
                <Select value={formData.field_type} onValueChange={(val) => setFormData({ ...formData, field_type: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Descricao curta *</Label>
              <Input required value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} placeholder="Ex: Objeto da contratacao" data-testid="doc-var-label" />
            </div>
            <div>
              <Label>Descricao detalhada</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Instrucoes para quem vai preencher" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valor padrao</Label>
                <Input value={formData.default_value} onChange={(e) => setFormData({ ...formData, default_value: e.target.value })} placeholder="Ex: 12" />
              </div>
              <div>
                <Label>Preenchimento automatico</Label>
                <Select value={formData.auto_fill_source || 'none'} onValueChange={(val) => setFormData({ ...formData, auto_fill_source: val === 'none' ? '' : val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AUTO_FILL_OPTIONS.map(o => <SelectItem key={o.value || 'none'} value={o.value || 'none'}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Document Types */}
            <div className="border border-[#E5E5EA] rounded-lg p-3 bg-[#FAFAFA]">
              <Label className="text-sm font-semibold">Tipos de Documento *</Label>
              <p className="text-[10px] text-[#AEAEB2] mb-2">Selecione em quais tipos esta variavel aparece e se e obrigatoria</p>
              <div className="space-y-2">
                {DOC_TYPES.map(dt => {
                  const config = formData.doc_types.find(d => d.doc_type === dt);
                  return (
                    <div key={dt} className="flex items-center gap-3">
                      <input type="checkbox" checked={!!config} onChange={() => toggleDocType(dt)} className="w-4 h-4 accent-[#AF2628]" data-testid={`doc-type-${dt.replace(/\s/g, '-')}`} />
                      <span className="text-sm flex-1">{dt}</span>
                      {config && (
                        <label className="flex items-center gap-1 text-[10px] text-[#6E6E73] cursor-pointer">
                          <input type="checkbox" checked={config.required} onChange={() => toggleRequired(dt)} className="w-3 h-3 accent-red-500" />
                          Obrigatoria
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#AF2628] hover:bg-[#961B22]" disabled={submitting} data-testid="doc-var-submit">
                {submitting ? 'Salvando...' : (editing ? 'Salvar' : 'Cadastrar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" />Excluir Variavel</DialogTitle>
            <DialogDescription>Excluir <code className="font-bold">{deleteTarget?.name}</code>?</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteTarget?.var_id)}>Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocVariablesPage;
